import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { forkJoin } from 'rxjs';
import { LibraryModule } from '../shared/lib/lib.module';
import { StaffAdminService } from '../core/services/staff-admin.service';
import { LocationService, LocationItem } from '../core/services/location.service';
import { StaffListItem } from '../core/models/staff.model';

type StaffFormMode = 'create' | 'edit' | 'view';

@Component({
  selector: 'staff-form-component, StaffFormComponent',
  standalone: true,
  templateUrl: './staff-form.component.html',
  styleUrls: ['./staff-form.component.css'],
  imports: [CommonModule, FormsModule, LibraryModule, RouterModule],
})
export class StaffFormComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly staffAdminService = inject(StaffAdminService);
  private readonly locationService = inject(LocationService);

  mode: StaffFormMode = 'create';
  staffId = '';
  isLoading = true;
  isSaving = false;
  errorMessage: string | null = null;
  successMessage: string | null = null;
  locationOptions: Array<{ label: string; value: string }> = [];

  sfId = '';
  staffName = '';
  email = '';
  status = true;
  selectedLocationIds: string[] = [];
  staffDetail: StaffListItem | null = null;

  ngOnInit(): void {
    const url = this.route.snapshot.url.map((segment) => segment.path);
    const staffId = this.route.snapshot.paramMap.get('staffId') ?? '';

    if (url.includes('create')) {
      this.mode = 'create';
    } else if (url.includes('edit') && staffId) {
      this.mode = 'edit';
      this.staffId = staffId;
    } else if (staffId) {
      this.mode = 'view';
      this.staffId = staffId;
    } else {
      void this.router.navigate(['/staff-management']);
      return;
    }

    this.loadInitialData();
  }

  get pageTitle(): string {
    switch (this.mode) {
      case 'create':
        return 'Create Staff';
      case 'edit':
        return 'Edit Staff';
      default:
        return 'View Staff';
    }
  }

  get isReadOnly(): boolean {
    return this.mode === 'view';
  }

  onBack(): void {
    void this.router.navigate(['/staff-management']);
  }

  onEdit(): void {
    void this.router.navigate(['/staff-management', this.staffId, 'edit']);
  }

  onSubmit(): void {
    if (this.isReadOnly || this.isSaving) {
      return;
    }

    this.errorMessage = null;
    this.successMessage = null;

    if (!this.staffName.trim() || !this.email.trim()) {
      this.errorMessage = 'Staff name and email are required.';
      return;
    }

    if (this.mode === 'create' && !this.sfId.trim()) {
      this.errorMessage = 'SF ID is required.';
      return;
    }

    this.isSaving = true;

    if (this.mode === 'create') {
      this.staffAdminService
        .createStaff({
          sfId: this.sfId.trim(),
          staffName: this.staffName.trim(),
          email: this.email.trim(),
          status: this.status,
          locationIds: this.selectedLocationIds,
        })
        .subscribe({
          next: () => {
            this.isSaving = false;
            void this.router.navigate(['/staff-management']);
          },
          error: (err) => {
            this.errorMessage = err?.error?.message ?? 'Unable to create staff member.';
            this.isSaving = false;
          },
        });
      return;
    }

    this.staffAdminService
      .updateStaff(this.staffId, {
        sfId: this.sfId.trim(),
        staffName: this.staffName.trim(),
        email: this.email.trim(),
        status: this.status,
        locationIds: this.selectedLocationIds,
      })
      .subscribe({
        next: () => {
          this.isSaving = false;
          void this.router.navigate(['/staff-management']);
        },
        error: (err) => {
          this.errorMessage = err?.error?.message ?? 'Unable to update staff member.';
          this.isSaving = false;
        },
      });
  }

  onDeactivate(): void {
    if (!this.staffId || this.mode === 'create' || !this.staffDetail?.status) {
      return;
    }

    const confirmed = window.confirm(`Deactivate ${this.staffDetail.staffName}?`);
    if (!confirmed) {
      return;
    }

    this.isSaving = true;
    this.errorMessage = null;

    this.staffAdminService.deactivateStaff(this.staffId).subscribe({
      next: () => {
        this.isSaving = false;
        this.successMessage = 'Staff member deactivated successfully.';
        this.loadStaff();
      },
      error: (err) => {
        this.errorMessage = err?.error?.message ?? 'Unable to deactivate staff member.';
        this.isSaving = false;
      },
    });
  }

  formatDate(value?: string | null): string {
    if (!value) {
      return '-';
    }
    return value.slice(0, 19).replace('T', ' ');
  }

  formatStatus(status: boolean): string {
    return status ? 'Active' : 'Inactive';
  }

  formatLocations(staff: StaffListItem): string {
    const names = (staff.locations ?? [])
      .map((location) => location.locationName)
      .filter(Boolean);
    return names.length > 0 ? names.join(', ') : '-';
  }

  private loadInitialData(): void {
    if (this.mode === 'create') {
      this.locationService.getActiveLocations().subscribe({
        next: (response) => {
          this.locationOptions = (response.locations ?? []).map((location: LocationItem) => ({
            label: location.locationName,
            value: location.id,
          }));
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
        },
      });
      return;
    }

    this.loadStaff();
  }

  private loadStaff(): void {
    this.isLoading = true;
    this.errorMessage = null;

    forkJoin({
      locations: this.locationService.getActiveLocations(),
      staff: this.staffAdminService.getStaff(this.staffId),
    }).subscribe({
      next: ({ locations, staff }) => {
        this.locationOptions = (locations.locations ?? []).map((location: LocationItem) => ({
          label: location.locationName,
          value: location.id,
        }));

        this.staffDetail = staff.staff;
        this.sfId = staff.staff.sfId ?? '';
        this.staffName = staff.staff.staffName ?? '';
        this.email = staff.staff.email ?? '';
        this.status = staff.staff.status ?? false;
        this.selectedLocationIds = (staff.staff.locations ?? []).map((location) => location.id);
        this.isLoading = false;
      },
      error: (err) => {
        this.errorMessage = err?.error?.message ?? 'Unable to load staff member.';
        this.isLoading = false;
      },
    });
  }
}
